/**
 * Validation and resolution for post-login return targets (`?next=`).
 *
 * This lives in shared/ rather than beside the middleware because it has TWO
 * consumers in two different runtimes: `src/middleware.ts` PRODUCES the value
 * on the edge runtime, and the login screen CONSUMES it in the browser. A
 * client component cannot import from middleware.ts without pulling
 * `next/server` into the client bundle, so the rule has to sit where both ends
 * can reach it.
 *
 * Both ends validate, deliberately. The producer's guarantee is worth nothing
 * to the consumer: `?next=` arrives in a user-controlled URL and anybody can
 * type one, so the login screen must re-check rather than trust that
 * middleware wrote it.
 */

/**
 * Returns the target if it is a plain same-origin path, else null.
 *
 * The rejected cases are all open-redirects that survive a naive
 * `startsWith("/")` check — protocol-relative URLs begin with a slash, and
 * some URL parsers normalise a backslash to a forward slash.
 */
export function safeNextTarget(target: string): string | null {
  if (!target.startsWith("/")) return null; // absolute URL -> leaves the site
  if (target.startsWith("//")) return null; // protocol-relative -> leaves the site
  if (/^\/\\/.test(target)) return null; // "/\evil.example" -> may leave the site
  return target;
}

/** Where an authenticated ORG member lands when there is no valid return target. */
export const DEFAULT_POST_LOGIN_ROUTE = "/dashboard";

/**
 * Where an authenticated PARTNER lands — the "Partner Overview" route the
 * VIP sidebar links to. A partner principal holds no org membership, so the
 * org dashboard's one data call (GET /api/spine/v1/sessions/my/) is denied
 * for them by design; landing them there made a red error card the first
 * screen of every partner session (#519).
 */
export const PARTNER_POST_LOGIN_ROUTE = "/partner";

/** True for the org dashboard root or any route beneath it. */
function isOrgDashboardTarget(target: string): boolean {
  const pathname = target.split(/[?#]/, 1)[0];
  return pathname === DEFAULT_POST_LOGIN_ROUTE ||
    pathname.startsWith(`${DEFAULT_POST_LOGIN_ROUTE}/`);
}

/**
 * Resolve where to send a freshly-authenticated user.
 *
 * Anything that fails validation falls back to the persona's landing route —
 * never to the raw value, and never to an error. A hostile `?next=` should
 * cost the user a redirect they didn't ask for, not a broken login.
 *
 * `isPartnerUser` is the same predicate the partner route guard uses
 * (AuthProvider's `user.partner != null`, itself mirroring the server's
 * auth/me partner block), so this resolver can never send a persona to a
 * surface its own guard would bounce them out of. It is the MIRROR of
 * app/(shell)/partner/layout.tsx, which already pushes non-partners to
 * /dashboard: partners are pushed the other way, and a `?next=` pointing
 * INTO /dashboard is overridden rather than honoured — middleware writes
 * that value from whatever path was requested, and honouring it would
 * reinstate exactly the error screen this rule exists to remove.
 */
export function resolvePostLoginRoute(
  rawNext: string | null | undefined,
  { isPartnerUser = false }: { isPartnerUser?: boolean } = {},
): string {
  const home = isPartnerUser ? PARTNER_POST_LOGIN_ROUTE : DEFAULT_POST_LOGIN_ROUTE;
  if (!rawNext) return home;
  const target = safeNextTarget(rawNext);
  if (!target) return home;
  if (isPartnerUser && isOrgDashboardTarget(target)) return PARTNER_POST_LOGIN_ROUTE;
  return target;
}
